import { useMemo } from 'react';

import { skipToken } from '@reduxjs/toolkit/dist/query';

import { CfiAddress } from '../cfis-api/cfisService';
import { useCfiQueryArgs, useCfis } from '../cfis-api/useCfis';
import { useLazyCfi } from '../cfis-api/useLazyCfi';
import {
  useFetchMileageTiersQuery,
  useLazyFetchMileageTiersQuery,
} from './mileageTiersService';

import { OptionalProduct } from '~/core/models';
import { createFetchProductsHook } from '~/core/utils/createFetchProductsHook';

interface useMileageTiersReturnType {
  /**
   * Mileage tier that matches the distance from user to CFI
   */
  matchingMileageTier?: OptionalProduct;
  isMileageTierResolved: boolean;
}
/**
 * * Parses {@link distanceRange} string with a form of min-max. And return and object with the min max as properties
 * @param distanceRange a string with the distance range written as min-max like 61-80 where 61 is the min and 80 the max
 * Also distance range may be written as 5+. Which means 5 is the min and max is Infinity
 * @example
 * Param of distanceRange = 61-80. Returns { min: 61, max: 80}
 * * @example
 * Param of distanceRange = 5+. Returns { min: 5, max: Infinity}
 */
export const parseDistanceRange = (
  distanceRange: string | null
): { min: number; max: number } => {
  // Fallback is the value used for [notUsed, min, max]. The first element of the array is not used for anything so is set to null.
  const fallback: [null, number, number] = [null, 0, Infinity];
  const [_, min, max] = /(\d+)[+-]+(\d*)/.exec(distanceRange ?? '') ?? fallback;

  return {
    min: +min,
    max: max ? +max : Infinity,
  };
};

const _useMileageTiers = createFetchProductsHook({
  apiQueryHook: useFetchMileageTiersQuery,
});

/**
 * Picks the mileage tier whose range contains the distance from the customer to the CFI.
 * Tier subcodes carry whole-mile bounds while the distance has decimals, so the distance
 * is rounded up before matching. When ranges overlap, the highest-priced tier wins.
 */
export const findMatchingMileageTier = (
  mileageTiers: OptionalProduct[],
  distance?: number
): OptionalProduct | undefined => {
  if (!distance) {
    return undefined;
  }

  const wholeMiles = Math.ceil(distance);

  return [...mileageTiers]
    .sort((a, b) => b.price - a.price)
    .find((mileageTier) => {
      const { min, max } = parseDistanceRange(mileageTier.qteSubCode);

      return wholeMiles >= min && wholeMiles <= max;
    });
};

export const useMileageTiers = (
  addressOverride?: CfiAddress
): useMileageTiersReturnType => {
  const { cfi } = useCfis(addressOverride);

  const { data: mileageTiers, catalogResolved } = _useMileageTiers({
    selectFromResult: (result) => ({
      data: result.currentData,
      catalogResolved: result.currentData !== undefined && !result.isError,
    }),
  });

  const matchingMileageTier = useMemo(
    () => findMatchingMileageTier(mileageTiers ?? [], cfi?.distance),
    [cfi?.distance, mileageTiers]
  );

  return {
    matchingMileageTier,
    isMileageTierResolved:
      catalogResolved &&
      typeof cfi?.distance === 'number' &&
      Number.isFinite(cfi.distance),
  };
};

export type MileageTierLookupResult =
  | { status: 'resolved'; matchingMileageTier?: OptionalProduct }
  | { status: 'unavailable' };

export const useLazyMileageTier = () => {
  const queryArgs = useCfiQueryArgs();
  const { getCfiUsingAddress } = useLazyCfi();
  const [fetchMileageTiers] = useLazyFetchMileageTiersQuery();

  const getMatchingMileageTier = async (
    address?: CfiAddress
  ): Promise<MileageTierLookupResult> => {
    if (queryArgs === skipToken || !queryArgs.params?.zipCode) {
      return { status: 'unavailable' };
    }

    const cfiResult = await getCfiUsingAddress(address);

    if (cfiResult.status === 'unavailable') {
      return cfiResult;
    }

    try {
      const { zipCode, storeId, prodQteGrpID } = queryArgs.params;
      const mileageTiers = await fetchMileageTiers(
        { params: { zipCode, storeId, prodQteGrpID } },
        true
      ).unwrap();

      return {
        status: 'resolved',
        matchingMileageTier: findMatchingMileageTier(
          mileageTiers,
          cfiResult.cfi.distance
        ),
      };
    } catch {
      return { status: 'unavailable' };
    }
  };

  return { getMatchingMileageTier };
};
